import { LitElement, html, nothing, type CSSResultGroup, type TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import styles from './styles.js';
import { emit } from '../utils/dom.utils.js';

// ─────────────────────────────────────────────────────────────────────────────
// Public Types
// ─────────────────────────────────────────────────────────────────────────────

export interface TreeNode {
  key?: string;
  label?: string;
  data?: unknown;
  icon?: string;
  expandedIcon?: string;
  collapsedIcon?: string;
  children?: TreeNode[];
  /** true = no toggle shown even with no children */
  leaf?: boolean;
  /** initial expanded state */
  expanded?: boolean;
  /** for custom node template */
  type?: string;
  /** default: true */
  selectable?: boolean;
  /** shows spinner on toggler (lazy loading) */
  loading?: boolean;
  styleClass?: string;
  draggable?: boolean;
  droppable?: boolean;
}

export type TreeSelectionMode = 'none' | 'single' | 'multiple' | 'checkbox';
export type TreeFilterMode    = 'lenient' | 'strict';

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

/**
 * uwc-tree
 *
 * Production-grade hierarchical tree component with full feature parity.
 *
 * @attr nodes               - Array of TreeNode objects.
 * @attr selection-mode      - none | single | multiple | checkbox. Default: none.
 * @attr value               - Selected node(s). Two-way via uwc-selection-change.
 * @attr filter              - Show a search filter input.
 * @attr filter-placeholder  - Placeholder for the filter input. Default: Search...
 * @attr filter-mode         - lenient | strict. Default: lenient.
 * @attr loading             - Show a loading overlay.
 * @attr scroll-height       - CSS max-height for scrollable tree.
 * @attr drag-drop           - Enable drag-and-drop reordering.
 * @attr propagate-selection-up   - Propagate selection to ancestor nodes (checkbox). Default: true.
 * @attr propagate-selection-down - Propagate selection to descendant nodes (checkbox). Default: true.
 * @attr empty-message       - Message shown when tree has no nodes. Default: No results found.
 *
 * @fires uwc-node-select       - Fired when a node is selected. Detail: { node, originalEvent }.
 * @fires uwc-node-unselect     - Fired when a node is unselected. Detail: { node, originalEvent }.
 * @fires uwc-node-expand       - Fired when a node is expanded. Detail: { node }.
 * @fires uwc-node-collapse     - Fired when a node is collapsed. Detail: { node }.
 * @fires uwc-lazy-load         - Fired when a loading node is expanded. Detail: { node }.
 * @fires uwc-selection-change  - Fired after any selection change. Detail: { value }.
 * @fires uwc-filter            - Fired when filter query changes. Detail: { query, filteredValue }.
 * @fires uwc-drag-start        - Fired when drag starts. Detail: { node }.
 * @fires uwc-drop              - Fired when node is dropped. Detail: { dragNode, dropNode, dropIndex }.
 *
 * @cssprop --uwc-tree-bg                  - Background color.
 * @cssprop --uwc-tree-border              - Border shorthand.
 * @cssprop --uwc-tree-radius              - Border radius.
 * @cssprop --uwc-tree-node-hover-bg       - Node hover background.
 * @cssprop --uwc-tree-node-selected-bg    - Selected node background.
 * @cssprop --uwc-tree-node-selected-color - Selected node text color.
 * @cssprop --uwc-tree-node-padding        - Node content padding.
 * @cssprop --uwc-tree-node-font-size      - Node font size.
 * @cssprop --uwc-tree-node-color          - Node text color.
 * @cssprop --uwc-tree-indent              - Children indentation. Default: 1.25rem.
 * @cssprop --uwc-tree-toggler-color       - Toggler icon color.
 * @cssprop --uwc-tree-icon-color          - Node icon color.
 * @cssprop --uwc-tree-filter-border       - Filter area border.
 * @cssprop --uwc-tree-loading-color       - Loading spinner color.
 * @cssprop --uwc-tree-empty-color         - Empty message color.
 */
export class UwcTree extends LitElement {

  static override styles: CSSResultGroup = styles;

  // ── Public properties ──────────────────────────────────────────────────────

  @property({ type: Array })
  nodes: TreeNode[] = [];

  @property({ attribute: 'selection-mode', reflect: true })
  selectionMode: TreeSelectionMode = 'none';

  @property({ type: Array })
  value: TreeNode[] | TreeNode | null = null;

  @property({ type: Boolean, reflect: true })
  filter = false;

  @property({ attribute: 'filter-placeholder' })
  filterPlaceholder = 'Search...';

  @property({ attribute: 'filter-mode' })
  filterMode: TreeFilterMode = 'lenient';

  @property({ type: Boolean, reflect: true })
  loading = false;

/**
   * Bootstrap Icons name for the expand/collapse toggle button.
   * The icon rotates 90° when the node is expanded, so `angle-right`
   * naturally becomes `angle-down`. Override to any icon name you prefer.
   * @default 'angle-right'
   */
  @property({ attribute: 'toggle-icon' })
  toggleIcon = 'angle-right';

  @property({ attribute: 'scroll-height' })
  scrollableHeight = '';

  @property({ attribute: 'drag-drop', type: Boolean, reflect: true })
  dragDrop = false;

  @property({ attribute: 'propagate-selection-up', type: Boolean })
  propagateSelectionUp = true;

  @property({ attribute: 'propagate-selection-down', type: Boolean })
  propagateSelectionDown = true;

  @property({ attribute: 'empty-message' })
  emptyMessage = 'No results found';

  // ── Internal state ──────────────────────────────────────────────────────────

  @state() private _expandedKeys  = new Set<string>();
  @state() private _selectedKeys  = new Set<string>();
  @state() private _loadingKeys   = new Set<string>();
  @state() private _filterQuery   = '';
  @state() private _dragOverKey: string | null = null;
  @state() private _focusedKey: string | null  = null;

  // ── Internal maps (not reactive, rebuilt in _initTree) ─────────────────────

  private _nodeMap:     Map<string, TreeNode>    = new Map();
  private _parentMap:   Map<string, string>      = new Map();
  private _childrenMap: Map<string, string[]>    = new Map();
  private _orderMap:    Map<string, string[]>    = new Map(); // parent-key → ordered child keys
  private _dragSourceKey: string | null          = null;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has('nodes')) {
      this._initTree();

      // Sync _expandedKeys from node.expanded:
      //   expanded: true  → always expand
      //   expanded: false → always collapse
      //   expanded: undefined → preserve existing user-toggled state
      const nextExpanded = new Set<string>();
      for (const [key, node] of this._nodeMap) {
        if (node.expanded === true) {
          nextExpanded.add(key);
        } else if (node.expanded !== false && this._expandedKeys.has(key)) {
          nextExpanded.add(key);
        }
      }
      this._expandedKeys = nextExpanded;

      // Clear _loadingKeys for nodes that have now received children
      if (this._loadingKeys.size > 0) {
        const next = new Set(this._loadingKeys);
        for (const key of this._loadingKeys) {
          const node = this._nodeMap.get(key);
          if (!node || !!(node.children && node.children.length > 0)) next.delete(key);
        }
        if (next.size !== this._loadingKeys.size) this._loadingKeys = next;
      }
    }
    if (changed.has('value')) {
      this._initSelection();
    }
  }

  // ── Tree initialisation ────────────────────────────────────────────────────

  private _initTree() {
    this._nodeMap.clear();
    this._parentMap.clear();
    this._childrenMap.clear();
    this._orderMap.clear();

    const walk = (nodes: TreeNode[], parentKey: string | null, pathPrefix: string) => {
      const siblingKeys: string[] = [];

      nodes.forEach((node, index) => {
        const key = node.key ?? (pathPrefix === '' ? String(index) : `${pathPrefix}-${index}`);

        if (!node.key) node.key = key;

        this._nodeMap.set(key, node);
        siblingKeys.push(key);

        if (parentKey !== null) {
          this._parentMap.set(key, parentKey);
        }

        if (node.children && node.children.length > 0) {
          const childKeys = walk(node.children, key, key);
          this._childrenMap.set(key, childKeys);
        } else {
          this._childrenMap.set(key, []);
        }
      });

      if (parentKey !== null) {
        this._orderMap.set(parentKey, siblingKeys);
      }

      return siblingKeys;
    };

    walk(this.nodes, null, '');
  }

  private _initSelection() {
    this._selectedKeys = new Set<string>();
    if (!this.value) return;

    const nodes = Array.isArray(this.value) ? this.value : [this.value];
    for (const n of nodes) {
      if (n.key) this._selectedKeys.add(n.key);
    }
  }

  // ── Visibility & filtering ─────────────────────────────────────────────────

  private _matchesFilter(node: TreeNode, key: string): boolean {
    const q = this._filterQuery.toLowerCase();
    if (!q) return true;

    if (this.filterMode === 'strict') {
      return (node.label?.toLowerCase().includes(q)) ?? false;
    }

    // Lenient: match self or any descendant
    if (node.label?.toLowerCase().includes(q)) return true;
    const childKeys = this._childrenMap.get(key) ?? [];
    return childKeys.some(ck => {
      const child = this._nodeMap.get(ck);
      return child ? this._matchesFilter(child, ck) : false;
    });
  }

  private _nodeVisible(key: string): boolean {
    const node = this._nodeMap.get(key);
    if (!node) return false;
    return this._matchesFilter(node, key);
  }

  private _ancestorsExpanded(key: string): boolean {
    let current = key;
    while (this._parentMap.has(current)) {
      const parentKey = this._parentMap.get(current)!;
      if (!this._expandedKeys.has(parentKey)) return false;
      current = parentKey;
    }
    return true;
  }

  private _getVisibleKeys(): string[] {
    const result: string[] = [];

    const walk = (keys: string[]) => {
      for (const k of keys) {
        const node = this._nodeMap.get(k);
        if (!node) continue;
        if (!this._nodeVisible(k)) continue;
        if (!this._ancestorsExpanded(k)) continue;

        result.push(k);

        const childKeys = this._childrenMap.get(k) ?? [];
        if (this._expandedKeys.has(k) && childKeys.length > 0) {
          walk(childKeys);
        }
      }
    };

    // Get root keys (nodes without parent)
    const rootKeys = this.nodes.map((n, i) => n.key ?? String(i));
    walk(rootKeys);
    return result;
  }

  // ── Toggle expand/collapse ─────────────────────────────────────────────────

  private _toggleExpand(node: TreeNode, key: string, e: Event) {
    e.stopPropagation();

    const isExpanded = this._expandedKeys.has(key);

    if (isExpanded) {
      this._expandedKeys = new Set(this._expandedKeys);
      this._expandedKeys.delete(key);
      emit(this, 'uwc-node-collapse', { node });
    } else {
      this._expandedKeys = new Set(this._expandedKeys);
      this._expandedKeys.add(key);
      emit(this, 'uwc-node-expand', { node });

      // Fire lazy-load only for explicitly lazy nodes that haven't loaded children yet
      const hasLoadedChildren = !!(node.children && node.children.length > 0);
      if ((node.leaf === false || node.loading === true) && !hasLoadedChildren) {
        this._loadingKeys = new Set([...this._loadingKeys, key]);
        emit(this, 'uwc-lazy-load', { node });
      }
    }
  }

  // ── Selection ──────────────────────────────────────────────────────────────

  private _isSelected(key: string): boolean {
    return this._selectedKeys.has(key);
  }

  private _isIndeterminate(key: string): boolean {
    if (this.selectionMode !== 'checkbox') return false;
    const childKeys = this._getAllDescendantKeys(key);
    if (childKeys.length === 0) return false;
    const selectedCount = childKeys.filter(k => this._selectedKeys.has(k)).length;
    return selectedCount > 0 && selectedCount < childKeys.length;
  }

  private _getAllDescendantKeys(key: string): string[] {
    const result: string[] = [];
    const queue = [...(this._childrenMap.get(key) ?? [])];
    while (queue.length > 0) {
      const k = queue.shift()!;
      result.push(k);
      queue.push(...(this._childrenMap.get(k) ?? []));
    }
    return result;
  }

  private _handleNodeClick(node: TreeNode, key: string, e: MouseEvent) {
    if (node.selectable === false || this.selectionMode === 'none') return;

    if (this.selectionMode === 'single') {
      if (this._isSelected(key)) {
        this._deselectNode(node, key, e);
      } else {
        this._selectedKeys = new Set<string>();
        this._selectNode(node, key, e);
      }
    } else if (this.selectionMode === 'multiple') {
      if (e.ctrlKey || e.metaKey) {
        if (this._isSelected(key)) {
          this._deselectNode(node, key, e);
        } else {
          this._selectNode(node, key, e);
        }
      } else if (e.shiftKey) {
        this._handleRangeSelection(key);
      } else {
        this._selectedKeys = new Set<string>();
        this._selectNode(node, key, e);
      }
    } else if (this.selectionMode === 'checkbox') {
      if (this._isSelected(key)) {
        this._deselectNode(node, key, e);
      } else {
        this._selectNode(node, key, e);
      }
    }
  }

  private _handleRangeSelection(endKey: string) {
    const visible = this._getVisibleKeys();
    const focusedIdx = this._focusedKey ? visible.indexOf(this._focusedKey) : 0;
    const endIdx = visible.indexOf(endKey);

    if (focusedIdx === -1 || endIdx === -1) return;

    const start = Math.min(focusedIdx, endIdx);
    const end   = Math.max(focusedIdx, endIdx);

    const newSelected = new Set(this._selectedKeys);
    for (let i = start; i <= end; i++) {
      newSelected.add(visible[i]);
    }
    this._selectedKeys = newSelected;
    this._emitSelectionChange();
  }

  private _selectNode(node: TreeNode, key: string, e?: Event) {
    const newSelected = new Set(this._selectedKeys);
    newSelected.add(key);

    if (this.selectionMode === 'checkbox') {
      if (this.propagateSelectionDown) this._propagateDown(key, true, newSelected);
      this._selectedKeys = newSelected;
      if (this.propagateSelectionUp) this._propagateUp(key);
    } else {
      this._selectedKeys = newSelected;
    }

    emit(this, 'uwc-node-select', { node, originalEvent: e });
    this._emitSelectionChange();
  }

  private _deselectNode(node: TreeNode, key: string, e?: Event) {
    const newSelected = new Set(this._selectedKeys);
    newSelected.delete(key);

    if (this.selectionMode === 'checkbox') {
      if (this.propagateSelectionDown) this._propagateDown(key, false, newSelected);
      this._selectedKeys = newSelected;
      if (this.propagateSelectionUp) this._propagateUp(key);
    } else {
      this._selectedKeys = newSelected;
    }

    emit(this, 'uwc-node-unselect', { node, originalEvent: e });
    this._emitSelectionChange();
  }

  private _propagateDown(key: string, select: boolean, target: Set<string>) {
    const childKeys = this._childrenMap.get(key) ?? [];
    for (const ck of childKeys) {
      if (select) {
        target.add(ck);
      } else {
        target.delete(ck);
      }
      this._propagateDown(ck, select, target);
    }
  }

  private _propagateUp(key: string) {
    let current = key;
    while (this._parentMap.has(current)) {
      const parentKey = this._parentMap.get(current)!;
      const childKeys = this._childrenMap.get(parentKey) ?? [];
      const allSelected  = childKeys.every(k => this._selectedKeys.has(k));
      const noneSelected = childKeys.every(k => !this._selectedKeys.has(k));

      const updated = new Set(this._selectedKeys);
      if (allSelected) {
        updated.add(parentKey);
      } else {
        updated.delete(parentKey);
      }
      this._selectedKeys = updated;

      if (noneSelected) break; // no need to walk further up if nothing is selected

      current = parentKey;
    }
  }

  private _emitSelectionChange() {
    let value: TreeNode[] | TreeNode | null = null;

    if (this.selectionMode === 'single') {
      const [firstKey] = this._selectedKeys;
      value = firstKey ? (this._nodeMap.get(firstKey) ?? null) : null;
    } else if (this.selectionMode === 'multiple' || this.selectionMode === 'checkbox') {
      value = [...this._selectedKeys]
        .map(k => this._nodeMap.get(k))
        .filter(Boolean) as TreeNode[];
    }

    this.value = value;
    emit(this, 'uwc-selection-change', { value });
  }

  // ── Keyboard navigation ────────────────────────────────────────────────────

  private _onKeydown(e: KeyboardEvent) {
    const visible = this._getVisibleKeys();
    if (visible.length === 0) return;

    const currentIdx = this._focusedKey ? visible.indexOf(this._focusedKey) : -1;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = currentIdx < visible.length - 1 ? currentIdx + 1 : 0;
        this._focusKey(visible[next]);
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = currentIdx > 0 ? currentIdx - 1 : visible.length - 1;
        this._focusKey(visible[prev]);
        break;
      }
      case 'ArrowRight': {
        e.preventDefault();
        if (!this._focusedKey) break;
        const node = this._nodeMap.get(this._focusedKey);
        if (!node) break;
        const hasChildren = (node.children && node.children.length > 0) || node.leaf === false;
        if (!hasChildren || node.leaf === true) break;

        if (!this._expandedKeys.has(this._focusedKey)) {
          this._toggleExpand(node, this._focusedKey, e);
        } else {
          // Focus first child
          const childKeys = this._childrenMap.get(this._focusedKey) ?? [];
          if (childKeys.length > 0) this._focusKey(childKeys[0]);
        }
        break;
      }
      case 'ArrowLeft': {
        e.preventDefault();
        if (!this._focusedKey) break;
        const node = this._nodeMap.get(this._focusedKey);
        if (!node) break;

        if (this._expandedKeys.has(this._focusedKey)) {
          this._toggleExpand(node, this._focusedKey, e);
        } else {
          // Focus parent
          const parentKey = this._parentMap.get(this._focusedKey);
          if (parentKey) this._focusKey(parentKey);
        }
        break;
      }
      case 'Home': {
        e.preventDefault();
        if (visible.length > 0) this._focusKey(visible[0]);
        break;
      }
      case 'End': {
        e.preventDefault();
        if (visible.length > 0) this._focusKey(visible[visible.length - 1]);
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        if (!this._focusedKey) break;
        const node = this._nodeMap.get(this._focusedKey);
        if (node) this._handleNodeClick(node, this._focusedKey, e as unknown as MouseEvent);
        break;
      }
      case '*': {
        // Expand all siblings
        e.preventDefault();
        if (!this._focusedKey) break;
        const parentKey = this._parentMap.get(this._focusedKey);
        const siblings = parentKey
          ? (this._childrenMap.get(parentKey) ?? [])
          : this.nodes.map((n, i) => n.key ?? String(i));

        const newExpanded = new Set(this._expandedKeys);
        for (const sib of siblings) {
          const sibNode = this._nodeMap.get(sib);
          const hasChildren = sibNode && ((sibNode.children && sibNode.children.length > 0) || sibNode.leaf === false);
          if (hasChildren && sibNode?.leaf !== true) {
            newExpanded.add(sib);
          }
        }
        this._expandedKeys = newExpanded;
        break;
      }
    }
  }

  private _focusKey(key: string) {
    this._focusedKey = key;
    this.updateComplete.then(() => {
      const el = this.shadowRoot?.querySelector(`[data-key="${CSS.escape(key)}"]`) as HTMLElement | null;
      el?.focus({ preventScroll: false });
    });
  }

  // ── Filter ─────────────────────────────────────────────────────────────────

  private _onFilterInput(e: Event) {
    this._filterQuery = (e as CustomEvent<{ value: string }>).detail.value;

    if (this.filterMode === 'lenient' && this._filterQuery) {
      // Auto-expand ancestors of matching nodes
      const newExpanded = new Set(this._expandedKeys);
      for (const [key, node] of this._nodeMap) {
        if (node.label?.toLowerCase().includes(this._filterQuery.toLowerCase())) {
          // Expand all ancestors
          let current = key;
          while (this._parentMap.has(current)) {
            const pk = this._parentMap.get(current)!;
            newExpanded.add(pk);
            current = pk;
          }
        }
      }
      this._expandedKeys = newExpanded;
    }

    const filteredValue = this.nodes.filter((n, i) => {
      const k = n.key ?? String(i);
      return this._nodeVisible(k);
    });

    emit(this, 'uwc-filter', { query: this._filterQuery, filteredValue });
  }

  // ── Drag and drop ──────────────────────────────────────────────────────────

  private _onDragStart(node: TreeNode, key: string, e: DragEvent) {
    if (!this.dragDrop) return;
    this._dragSourceKey = key;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', key);
    }
    emit(this, 'uwc-drag-start', { node });
  }

  private _onDragOver(key: string, e: DragEvent) {
    if (!this.dragDrop || !this._dragSourceKey || this._dragSourceKey === key) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    this._dragOverKey = key;
  }

  private _onDragLeave(_key: string, _e: DragEvent) {
    this._dragOverKey = null;
  }

  private _onDrop(dropNode: TreeNode, dropKey: string, e: DragEvent) {
    if (!this.dragDrop || !this._dragSourceKey) return;
    e.preventDefault();

    const dragKey  = this._dragSourceKey;
    const dragNode = this._nodeMap.get(dragKey);

    this._dragOverKey   = null;
    this._dragSourceKey = null;

    if (!dragNode || dragKey === dropKey) return;

    // Remove from source parent
    const srcParentKey = this._parentMap.get(dragKey);
    if (srcParentKey !== undefined) {
      const srcParent = this._nodeMap.get(srcParentKey);
      if (srcParent?.children) {
        const idx = srcParent.children.findIndex(c => c.key === dragKey);
        if (idx !== -1) srcParent.children.splice(idx, 1);
      }
    } else {
      // Root level
      const idx = this.nodes.findIndex(n => n.key === dragKey);
      if (idx !== -1) this.nodes.splice(idx, 1);
    }

    // Insert into drop target's children
    if (!dropNode.children) dropNode.children = [];
    const dropIndex = dropNode.children.length;
    dropNode.children.push(dragNode);

    // Re-init tree after structure change, then auto-expand the drop target
    this._initTree();
    if (!this._expandedKeys.has(dropKey)) {
      this._expandedKeys = new Set([...this._expandedKeys, dropKey]);
    }
    this.requestUpdate('nodes');

    emit(this, 'uwc-drop', { dragNode, dropNode, dropIndex });
  }

  // ── Render helpers ─────────────────────────────────────────────────────────

  private _renderNodes(nodes: TreeNode[], level: number, siblingCount?: number): TemplateResult[] {
    const filtered = this._filterQuery
      ? nodes.filter(n => n.key != null && this._nodeVisible(n.key))
      : nodes;
    const count = siblingCount ?? filtered.length;
    return filtered.map((node, index) => this._renderNode(node, level, index, count));
  }

  private _renderNode(node: TreeNode, level: number, index: number, siblingCount: number): TemplateResult {
    const key       = node.key!;
    const isExpanded = this._expandedKeys.has(key);
    const isSelected = this._isSelected(key);
    const isIndeterminate = this._isIndeterminate(key);
    const isFocused = this._focusedKey === key;
    const isDragOver = this._dragOverKey === key;
    const isDisabled = node.selectable === false && this.selectionMode !== 'none';

    const isLoading  = this._loadingKeys.has(key);
    const actuallyHasChildren = !!(node.children && node.children.length > 0);
    const showToggler = actuallyHasChildren || node.leaf === false || node.loading === true;

    const nodeContentClasses = classMap({
      'uwc-tree__node-content': true,
      'uwc-tree__node-content--selected':    isSelected,
      'uwc-tree__node-content--focused':     isFocused,
      'uwc-tree__node-content--drop-target': isDragOver,
      'uwc-tree__node-content--disabled':    isDisabled,
    });


    // Compute icon: use expandedIcon/collapsedIcon when expanded/collapsed
    const iconName = isExpanded
      ? (node.expandedIcon ?? node.icon)
      : (node.collapsedIcon ?? node.icon);

    // ARIA values
    const ariaExpanded = showToggler ? String(isExpanded) : nothing;
    const ariaSelected = this.selectionMode !== 'checkbox'
      ? (this.selectionMode !== 'none' ? String(isSelected) : nothing)
      : nothing;
    const ariaChecked = this.selectionMode === 'checkbox'
      ? (isSelected ? 'true' : isIndeterminate ? 'mixed' : 'false')
      : nothing;

    return html`
      <li
        role="treeitem"
        class="uwc-tree__node ${node.styleClass ?? ''}"
        aria-expanded=${ariaExpanded}
        aria-selected=${ariaSelected}
        aria-checked=${ariaChecked}
        aria-level=${level}
        aria-setsize=${siblingCount}
        aria-posinset=${index + 1}
        data-key=${key}
        tabindex=${isFocused ? 0 : -1}
        @focus=${() => { this._focusedKey = key; }}
      >
        <div
          class=${nodeContentClasses}
          @click=${(e: MouseEvent) => this._handleNodeClick(node, key, e)}
          .draggable=${this.dragDrop && node.draggable !== false}
          @dragstart=${this.dragDrop ? (e: DragEvent) => this._onDragStart(node, key, e) : nothing}
          @dragover=${this.dragDrop ? (e: DragEvent) => this._onDragOver(key, e) : nothing}
          @dragleave=${this.dragDrop ? (e: DragEvent) => this._onDragLeave(key, e) : nothing}
          @drop=${this.dragDrop ? (e: DragEvent) => this._onDrop(node, key, e) : nothing}
        >
          ${showToggler
            ? html`
              <span
                draggable="false"
                class=${classMap({
                  'uwc-tree__toggler-icon':           true,
                  'uwc-tree__toggler-icon--expanded': isExpanded && !isLoading,
                })}
              >
                <uwc-button
                  icon=${isLoading ? nothing : this.toggleIcon}
                  ?loading=${isLoading}
                  icon-only
                  text
                  rounded
                  size="small"
                  variant="secondary"
                  aria-label=${isExpanded ? 'Collapse' : 'Expand'}
                  .tabindex=${-1}
                  @click=${(e: Event) => e.stopPropagation()}
                  @uwc-click=${(e: Event) => this._toggleExpand(node, key, e)}
                ></uwc-button>
              </span>
            `
            : html`<span class="uwc-tree__toggler-placeholder" draggable="false" aria-hidden="true"></span>`
          }

          ${this.selectionMode === 'checkbox' ? html`
            <span class="uwc-tree__checkbox">
              <uwc-checkbox
                .checked=${isSelected}
                .indeterminate=${isIndeterminate}
                tabindex="-1"
                @uwc-change=${(e: Event) => {
                  e.stopPropagation();
                  if (isSelected || isIndeterminate) {
                    this._deselectNode(node, key, e);
                  } else {
                    this._selectNode(node, key, e);
                  }
                }}
              ></uwc-checkbox>
            </span>
          ` : nothing}

          ${iconName ? html`
            <span class="uwc-tree__icon" aria-hidden="true">
              <uwc-icon name=${iconName}></uwc-icon>
            </span>
          ` : nothing}

          <span class="uwc-tree__label">${node.label ?? ''}</span>
        </div>

        ${(showToggler || (node.children && node.children.length > 0)) && isExpanded && node.children && node.children.length > 0
          ? html`
            <ul role="group" class="uwc-tree__children" aria-label=${ifDefined(node.label)}>
              ${this._renderNodes(node.children, level + 1, node.children.length)}
            </ul>
          `
          : nothing
        }
      </li>
    `;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  override render() {
    const hasNodes = this.nodes.length > 0;

    const containerClasses = classMap({
      'uwc-tree': true,
      'uwc-tree--scrollable': !!this.scrollableHeight,
    });

    const containerStyle = this.scrollableHeight
      ? `max-height: ${this.scrollableHeight};`
      : '';

    // Filter visible root nodes
    const visibleRootNodes = this._filterQuery
      ? this.nodes.filter((n, i) => {
          const k = n.key ?? String(i);
          return this._nodeVisible(k);
        })
      : this.nodes;

    return html`
      <div
        class=${containerClasses}
        style=${containerStyle || nothing}
      >
        ${this.filter ? html`
          <div class="uwc-tree__filter">
            <uwc-inputtext
              placeholder=${this.filterPlaceholder}
              .value=${this._filterQuery}
              aria-label="Filter tree nodes"
              @uwc-input=${this._onFilterInput}
            >
              <uwc-icon slot="prefix" name="search"></uwc-icon>
            </uwc-inputtext>
          </div>
        ` : nothing}

        ${this.loading ? html`
          <div class="uwc-tree__loading" role="status" aria-label="Loading">
            <span class="uwc-tree__spinner" aria-hidden="true"></span>
          </div>
        ` : nothing}

        ${!hasNodes && !this.loading ? html`
          <div class="uwc-tree__empty" role="status">${this.emptyMessage}</div>
        ` : nothing}

        ${hasNodes ? html`
          <ul
            role="tree"
            class="uwc-tree__nodes"
            aria-multiselectable=${this.selectionMode === 'multiple' || this.selectionMode === 'checkbox'
              ? 'true'
              : 'false'}
            @keydown=${this._onKeydown}
          >
            ${repeat(
              visibleRootNodes,
              (n, i) => n.key ?? String(i),
              (n, i) => this._renderNode(n, 1, i, visibleRootNodes.length),
            )}
          </ul>
        ` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-tree': UwcTree; }
}
